// ** Modules
import React from 'react';

// ** UI
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

// ** Components
import CircleHoverCta from '@/components/common/CircleHoverCta';
import StrapiImageRenderer from '@/components/strapi/StrapiImageRenderer';
import UnderlineHoverCta from '@/components/common/UnderlineHoverCta';
import ArrowSlideHoverCta from '@/components/common/ArrowSlideHoverCta';
import { HERO_HEADER_DEFAULTS_TYPED } from '@/components/common/defaults';

// ** Context
import { useHeaderOverride } from '@/context/HeaderOverrideContext';

// ** Utils
import { buildPathFromRelationLink } from '@/utils/ui/navigationUtils';

// ** Types
import type { SxProps, Theme } from '@mui/material/styles';
import type { StrapiComponentProps } from '@/types/strapi';
import type { ComponentBlocksHero as BlocksHeroProps } from '@/types/graphql';

type InjectedHeroProps = {
	isMobile: boolean;
};
type HomepageVariantProps = StrapiComponentProps<BlocksHeroProps> & InjectedHeroProps;

const SPACING = {
	eyebrowToHeadline: { xs: 3, lg: 4 },
	headlineToCopy: { xs: 4, lg: 8 },
	copyToCta: { xs: 6, lg: 8 }
} as const;

const ROOT_SX = {
	position: 'relative',
	display: { xs: 'block', lg: 'flex' },
	minHeight: { xs: 'auto', lg: 900 }
} as const;

const LEFT_IMAGE_SX = {
	position: 'relative',
	flex: { lg: '1 1 50%' },
	width: { xs: '100%', lg: '50%' },
	height: { xs: 420, sm: 520, lg: 900 },
	overflow: 'hidden'
} as const;

const RIGHT_PANEL_SX = {
	position: 'relative',
	flex: { lg: '1 1 50%' },
	width: { xs: '100%', lg: '50%' },
	minHeight: { xs: 'auto', lg: 900 }
} as const;

const RIGHT_PANEL_CONTENT_SX: SxProps<Theme> = (theme) => ({
	height: '100%',
	display: 'flex',
	alignItems: { xs: 'flex-start', lg: 'center' },
	pl: theme.layout.heroPanelPx,
	pr: theme.layout.heroPanelPx,
	pt: { xs: 10, lg: 0 },
	pb: { xs: 8, lg: 0 }
});

// TODO: Create defaults for these props
const HomepageVariant = ({
	host,
	heroImage,
	eyebrow = HERO_HEADER_DEFAULTS_TYPED.eyebrow,
	headline = HERO_HEADER_DEFAULTS_TYPED.headline,
	copy = HERO_HEADER_DEFAULTS_TYPED.copy,
	linkLabel = HERO_HEADER_DEFAULTS_TYPED.linkLabel,
	linkHref,
	heroColorVariant,
	ctaVariant,
	showMenu,
	linkHrefExternal
}: HomepageVariantProps) => {
	const theme = useTheme();
	const themeMode = theme.palette.mode;
	const { setOverride } = useHeaderOverride();

	React.useEffect(() => {
		// 1. REGISTER: The moment this component mounts, it tells the header how to look
		setOverride({
			appBarVariant: heroColorVariant?.toLowerCase() === 'light' ? 'light' : 'dark',
			showMenu: !!showMenu
		});

		// 2. UNREGISTER: The moment this component leaves the DOM, it clears the override
		return () => setOverride(null);
	}, [heroColorVariant, showMenu, setOverride]);

	const contentMaxWidth = {
		xs: '100%',
		lg: theme.layout.heroPanelContentMaxWidthPx
	};

	const textColor =
		heroColorVariant?.toLowerCase() === 'light'
			? (theme.palette.text.secondary ?? theme.palette.text.primary)
			: theme.palette.common.white;
	const headlineColor = heroColorVariant?.toLowerCase() === 'light' ? theme.palette.primary.main : textColor;
	const rightPanelBg =
		heroColorVariant?.toLowerCase() === 'light' ? theme.palette.heroLightBackground.main : theme.palette.grey[600];

	const ctaSharedProps = {
		href: linkHrefExternal ? linkHrefExternal : buildPathFromRelationLink(linkHref),
		label: linkLabel as string,
		color: textColor,
		typographyVariant: 'body-md' as const,
		fontWeight: 500
	};

	const cta =
		ctaVariant?.toLowerCase() === 'underline' ? (
			<UnderlineHoverCta {...ctaSharedProps} />
		) : ctaVariant?.toLowerCase() === 'arrow_slide' ? (
			<ArrowSlideHoverCta {...ctaSharedProps} />
		) : (
			<CircleHoverCta {...ctaSharedProps} />
		);

	return (
		<Box sx={ROOT_SX}>
			<Box sx={LEFT_IMAGE_SX}>
				<StrapiImageRenderer
					host={host}
					imageData={heroImage}
					themeMode={themeMode}
					alt={heroImage?.alternativeText || 'Hero Image'}
					fill
					loading="eager"
					sizes="(max-width: 1200px) 100vw, 50vw"
					// style={{ objectFit: 'cover', objectPosition: 'center' }}
				/>
			</Box>

			<Box
				sx={{
					...RIGHT_PANEL_SX,
					backgroundColor: rightPanelBg,
					color: textColor
				}}
			>
				<Box sx={RIGHT_PANEL_CONTENT_SX}>
					<Stack sx={{ maxWidth: contentMaxWidth }}>
						{eyebrow && (
							<Typography variant="label" sx={{ color: textColor }}>
								{eyebrow}
							</Typography>
						)}

						{headline && (
							<Typography variant="display-lg" sx={{ mt: SPACING.eyebrowToHeadline, color: headlineColor }}>
								{headline}
							</Typography>
						)}

						{copy && (
							<Typography
								variant="body-md"
								sx={{
									mt: SPACING.headlineToCopy,
									color: textColor,
									whiteSpace: 'pre-line'
								}}
							>
								{copy}
							</Typography>
						)}

						{linkHref && <Box sx={{ mt: SPACING.copyToCta }}>{cta}</Box>}
					</Stack>
				</Box>
			</Box>
		</Box>
	);
};

export default HomepageVariant;
